/**
 * `VisitHeadline` — the "AI agents visited your site N times this week"
 * sentence that used to live in the Readiness page header.
 *
 * Moved 2026-10-02: the readiness header now carries the verdict
 * ("Ready for AI agents.", etc.); the visit count + top-agent subline
 * belongs where the merchant looks for traffic — the Agents view.
 *
 * Platform-kind pick ("site" vs "store"):
 *   - Try the readiness-page cache first (synchronous, local).
 *   - Fall back to a visibility fetch (DB-only, fast).
 *   - Default to "site" while either is pending.
 */

import { useEffect, useState } from 'react';

import type { AgentOverviewResponse, AgentRollupRange, AgentKind } from '@sill/types/api';
import type { ReadinessPlatformKind, SiteId } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { derivePlatformKind } from '../../lib/platform-kind.js';
import { useC } from '../../lib/theme.js';
import { readReadinessCache } from '../readiness/readiness-cache.js';
import { fmtInt, rangePhrase, sansFont } from './format.js';

export interface VisitHeadlineProps {
  readonly overview: AgentOverviewResponse | null;
  readonly site_id: SiteId | null;
  readonly range: AgentRollupRange;
  readonly agent_kind: AgentKind;
}

function pluralNounForKind(kind: ReadinessPlatformKind): 'site' | 'store' {
  return kind === 'store' ? 'store' : 'site';
}

export function VisitHeadline({ overview, site_id, range, agent_kind }: VisitHeadlineProps): JSX.Element | null {
  const api = useApi();
  const C = useC();
  // Platform-kind seed: readiness cache has the install bundle (and
  // sometimes a derived kind in the signals) — use it instantly if
  // present, fall back to a visibility fetch otherwise.
  const [kind, setKind] = useState<ReadinessPlatformKind>(() => {
    if (site_id === null) return 'website';
    const cached = readReadinessCache(site_id);
    if (cached === null) return 'website';
    return derivePlatformKind({ install: cached.install, visibility: null });
  });

  useEffect(() => {
    if (site_id === null) return;
    const cached = readReadinessCache(site_id);
    if (cached !== null && cached.install !== null) {
      // We already seeded above. Nothing else to do.
      return;
    }
    let cancelled = false;
    void api.visibility.getReport(site_id).then(
      (r) => {
        if (cancelled) return;
        setKind(derivePlatformKind({ install: null, visibility: r }));
      },
      () => {
        // Fail-soft: keep "site" default.
      },
    );
    return () => { cancelled = true; };
  }, [api, site_id]);

  // Only render for AI-filter + windows where the overview carries a
  // traffic number. The current data layer already fetches overview
  // for the Overview tab and Demand tab; when the Agents tab is
  // selected overview may be `null` — in that case we render nothing.
  if (overview === null) return null;
  const visits = overview.totals.traffic;
  if (visits === 0 && overview.prior.traffic === 0) return null;

  // The sentence is specific to the agent-kind filter. Only reword
  // for the AI filter — the other filters have their own semantics.
  const noun = pluralNounForKind(kind);
  const subject =
    agent_kind === 'ai' ? 'AI agents'
    : agent_kind === 'bots' ? 'Crawlers and tools'
    : agent_kind === 'humans' ? 'People'
    : 'Callers';
  const headline =
    visits === 0
      ? `No ${subject.toLowerCase()} visited your ${noun} ${rangePhrase(range)}.`
      : `${subject} visited your ${noun} ${fmtInt(visits)} ${visits === 1 ? 'time' : 'times'} ${rangePhrase(range)}.`;

  // Top 3 agents in the window, by total page loads.
  const top = overview.series.agents
    .map((a) => ({
      name: a.inferred_name ?? a.agent_id ?? a.agent_key,
      total: a.traffic.reduce((s, v) => s + v, 0),
    }))
    .filter((a) => a.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 3);
  const subline = top.length > 0
    ? top.map((a) => `${a.name} ${fmtInt(a.total)}`).join(', ')
    : null;

  return (
    <div
      data-testid="agents-visit-headline"
      style={{ marginTop: 10, fontFamily: sansFont, fontSize: 15, color: C.body, lineHeight: 1.5, maxWidth: 640 }}
    >
      {headline}
      {subline !== null ? (
        <>
          <span aria-hidden="true" style={{ margin: '0 6px', color: C.dim }}>·</span>
          <span style={{ color: C.muted }}>{subline}</span>
        </>
      ) : null}
    </div>
  );
}
